import { DistributionSlice } from '../models/dashboard.model';
import {
  ChamadoListItem,
  DayBucket,
  EstimativaBloco,
  EstimativaItem,
  QualidadeBloco,
  TimeEntryMetricsContext,
  TimeEntryMetricsModel,
  TimeEntryRow,
} from '../models/time-entry-metrics.model';
import { contarPercentual, metricCard, parseDate, topNComOutros } from './dashboard-metrics.util';

/**
 * Motor das métricas de apontamentos de `/chamados/apontamentos/historico`, no padrão "dashboard
 * derivado da listagem" (ADR-0011/0012).
 *
 * PURO: sem `inject()`, sem HTTP, sem `new Date()` interno (a referência de "agora" vem do
 * contexto). É o que permite compilar este arquivo sozinho e rodar asserções com `node` (how-to §5).
 *
 * ⚠️ As horas são as do RECORTE FILTRADO, não o histórico completo do chamado. No bloco de
 * estimativa isso subestima o consumo — o único sinal sempre verdadeiro é o inverso: se só o
 * recorte já passou da estimativa, estourou.
 */

const MIN_MS = 60 * 1000;

/** Acima disto um bloco contínuo é suspeito de esquecimento de encerramento. */
const BLOCO_LONGO_MIN = 8 * 60;
/** Abaixo disto é provável clique errado (iniciar e encerrar em seguida). */
const BLOCO_CURTO_MIN = 5;
/** Descrição menor que isto não conta o que foi feito. */
const DESCRICAO_CURTA_CHARS = 20;
/** Quantas fatias antes de agrupar o resto em "Outros". */
const TOP_N = 10;

/**
 * Onde o resumo do chamado é cortado nas listas. Medido contra a largura do
 * modal: acima disso a linha empurra a duração para fora ou quebra em duas.
 * O corte é feito AQUI, não no template — quem monta o modelo formata.
 */
const RESUMO_MAX_CHARS = 60;

/** Rota-alvo das fatias clicáveis: a própria tela, que traduz em filtro local. */
const ROTA = '/chamados/apontamentos/historico';

const TIPOS: Record<string, string> = {
  M: 'Manutenção',
  E: 'Erro',
  I: 'Inovação',
  A: 'Acompanhamento',
};
const ATENDIMENTOS: Record<string, string> = { I: 'Interno', E: 'Externo' };

/**
 * O `type` chega ora como código, ora como rótulo já traduzido, dependendo do
 * endpoint que montou a linha — daí a tradução aceitar os dois e devolver o
 * próprio valor quando não reconhece, em vez de virar "Outros" silenciosamente.
 */
function rotulo(dicionario: Record<string, string>, valor: string | undefined): string {
  if (!valor) return 'Não informado';
  return dicionario[valor] ?? valor;
}

/** `12h 30min`, `45min`, `0min`. Nunca notação decimal — ninguém aponta "1,75h". */
export function formatarDuracao(minutos: number): string {
  const total = Math.max(0, Math.round(minutos));
  const horas = Math.floor(total / 60);
  const resto = total % 60;
  if (horas > 0) return `${horas}h ${resto.toString().padStart(2, '0')}min`;
  return `${resto}min`;
}

/**
 * Resumo pronto para exibir: uma linha, sem quebra, cortado em
 * `RESUMO_MAX_CHARS`. Corta na última palavra inteira para não terminar no meio
 * de uma sílaba, e nunca devolve string vazia — linha em branco na lista parece
 * defeito, e o leitor não consegue distinguir de um dado que não carregou.
 */
function resumoCurto(texto: string | undefined): string {
  const limpo = (texto ?? '').replace(/\s+/g, ' ').trim();
  if (!limpo) return 'Sem resumo';
  if (limpo.length <= RESUMO_MAX_CHARS) return limpo;

  const cortado = limpo.slice(0, RESUMO_MAX_CHARS);
  const ultimoEspaco = cortado.lastIndexOf(' ');
  const base = ultimoEspaco > RESUMO_MAX_CHARS * 0.6 ? cortado.slice(0, ultimoEspaco) : cortado;
  return `${base.trimEnd()}…`;
}

/** Chave de dia no MESMO formato das colunas do kanban (`AAAAMMDD`). */
function chaveDia(data: Date): string {
  return (
    data.getFullYear().toString().padStart(4, '0') +
    (data.getMonth() + 1).toString().padStart(2, '0') +
    data.getDate().toString().padStart(2, '0')
  );
}

function rotuloDia(data: Date): string {
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Abreviação de 3 letras do dia da semana, minúscula ("seg", "ter"…). O
 * `Intl` devolve "seg." com ponto final e às vezes maiúscula por locale — tira
 * as duas coisas para o rótulo ficar previsível embaixo da data.
 */
function rotuloDiaSemana(data: Date): string {
  return data.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '').toLowerCase();
}

function meiaNoite(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate());
}

/** Segunda a sexta entre as duas pontas, inclusive. Não conhece feriado. */
function diasUteis(inicio: Date, fim: Date): number {
  let dias = 0;
  const cursor = meiaNoite(inicio);
  const limite = meiaNoite(fim);
  while (cursor.getTime() <= limite.getTime()) {
    const diaSemana = cursor.getDay();
    if (diaSemana !== 0 && diaSemana !== 6) dias++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return dias;
}

/** Uma linha utilizável: data de início legível. O resto pode faltar. */
interface LinhaValida {
  row: TimeEntryRow;
  inicio: Date;
  fim: Date;
  /** `true` = ainda rodando; a duração foi medida até `ctx.hoje`. */
  aberto: boolean;
  minutos: number;
  dia: string;
}

function incrementar(mapa: Map<string, number>, chave: string, valor: number): void {
  mapa.set(chave, (mapa.get(chave) ?? 0) + valor);
}

/**
 * Fatias com peso em MINUTOS (não contagem): a largura da barra representa
 * tempo, e o texto de cada fatia sai formatado como duração. Sem isso um
 * cliente com 40 apontamentos de 5 minutos pareceria maior que um com 3
 * apontamentos de 4 horas.
 */
function fatiasPorDuracao(
  minutosPorChave: Map<string, number>,
  rotulos: Map<string, string>,
  totalMinutos: number,
  filtroDe: (chave: string) => Record<string, unknown> | undefined
): DistributionSlice[] {
  return topNComOutros(minutosPorChave, totalMinutos, TOP_N, filtroDe, ROTA, {
    rotuloDe: chave => rotulos.get(chave) ?? chave,
    formatarValor: formatarDuracao,
  });
}

/** Distribuição fixa (tipo, atendimento, departamento): sem "Outros", sem clique. */
function fatiasFixas(
  minutosPorChave: Map<string, number>,
  dicionario: Record<string, string>,
  totalMinutos: number
): DistributionSlice[] {
  return [...minutosPorChave.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([chave, minutos]) => ({
      key: chave,
      label: rotulo(dicionario, chave),
      count: minutos,
      percent: contarPercentual(minutos, totalMinutos),
      target: null,
      formattedValue: formatarDuracao(minutos),
    }));
}

/**
 * Forma estrutural do que `ticketcenter/ticket-logs?include_ticket=1` devolve, declarada aqui (sem
 * importar os modelos do app) para o arquivo continuar compilável sozinho. Todos os campos
 * opcionais: neste backend já houve interface declarando campo que a API não envia.
 */
export interface TimeEntryApiLog {
  id?: string;
  start_date?: string | Date | number | null;
  end_date?: string | Date | number | null;
  type?: string;
  attendance_type?: string;
  description?: string;
  user_id?: string;
  user?: { id?: string; name?: string } | null;
  system_id?: string;
  system?: { id?: string; description?: string } | null;
  ticket_id?: string;
  ticket?: {
    id?: string;
    ticket_number_teorema?: string;
    summary?: string;
    problem?: string;
    customer_name?: string;
    system?: { id?: string; description?: string } | null;
    estimated_effort?: number | string | null;
    commercial_estimated_effort?: number | string | null;
    /** Mesmo campo da coluna "Responsável" da listagem de chamados. */
    responsible?: { id?: string; name?: string } | null;
  } | null;
}

/**
 * A data pode chegar como string ISO (o caso real da API), mas os modelos legados
 * declaram `Date | number` — e o motor só sabe ler string via `parseDate`, que
 * recusa formato ambíguo de propósito. Normalizar aqui, na fronteira.
 */
function paraIso(valor: string | Date | number | null | undefined): string | null {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor === 'string') return valor;
  const data = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}

/** Estimativa pode chegar number ou string; string vazia não é zero. */
function paraNumero(valor: number | string | null | undefined): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const numero = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Achata a resposta da API na linha que o motor consome. O `system` do
 * apontamento tem precedência sobre o do chamado: é o que aquele trabalho
 * específico tocou, não o módulo padrão do chamado.
 */
export function flattenTimeEntries(logs: TimeEntryApiLog[]): TimeEntryRow[] {
  return logs.map(log => {
    const sistema = log.system ?? log.ticket?.system ?? null;
    return {
      id: log.id ?? '',
      startDate: paraIso(log.start_date),
      endDate: paraIso(log.end_date),
      type: log.type,
      attendanceType: log.attendance_type,
      responsibleId: log.ticket?.responsible?.id,
      responsibleName: log.ticket?.responsible?.name,
      description: log.description,
      userId: log.user_id ?? log.user?.id,
      userName: log.user?.name,
      systemId: sistema?.id,
      systemName: sistema?.description,
      ticketId: log.ticket_id ?? log.ticket?.id,
      ticketNumber: log.ticket?.ticket_number_teorema,
      // `problem` como reserva: chamado aberto pelo HelpDesk às vezes não tem
      // resumo preenchido, e aí o texto do problema é a única descrição que existe.
      ticketSummary: log.ticket?.summary || log.ticket?.problem,
      customerName: log.ticket?.customer_name,
      estimatedEffort: paraNumero(log.ticket?.estimated_effort),
      commercialEstimatedEffort: paraNumero(log.ticket?.commercial_estimated_effort),
    };
  });
}

export function buildTimeEntryMetrics(
  rows: TimeEntryRow[],
  ctx: TimeEntryMetricsContext
): TimeEntryMetricsModel {
  const agora = ctx.hoje.getTime();

  // Linha sem data de início legível não entra em conta nenhuma: `parseDate`
  // recusa formato ambíguo em vez de adivinhar, e um total montado sobre data
  // adivinhada erra sem avisar.
  const linhas: LinhaValida[] = [];
  for (const row of rows) {
    const inicio = parseDate(row.startDate);
    if (!inicio) continue;
    const fimData = parseDate(row.endDate);
    const aberto = !fimData;
    const fim = fimData ?? new Date(agora);
    const minutos = Math.max(0, (fim.getTime() - inicio.getTime()) / MIN_MS);
    linhas.push({ row, inicio, fim, aberto, minutos, dia: chaveDia(inicio) });
  }

  if (linhas.length === 0) {
    return vazio(ctx);
  }

  linhas.sort((a, b) => a.inicio.getTime() - b.inicio.getTime());

  const totalMinutos = linhas.reduce((soma, l) => soma + l.minutos, 0);
  const emAberto = linhas.filter(l => l.aberto).length;

  // --- agrupamentos -------------------------------------------------------
  const minutosPorDia = new Map<string, number>();
  const chamadosPorDia = new Map<string, Set<string>>();
  const minutosPorDiaChamado = new Map<string, number>();
  const minutosPorTipo = new Map<string, number>();
  const minutosPorAtendimento = new Map<string, number>();
  const minutosPorResponsavel = new Map<string, number>();
  const minutosPorCliente = new Map<string, number>();
  const minutosPorSistema = new Map<string, number>();
  const minutosPorUsuario = new Map<string, number>();
  const minutosPorChamado = new Map<string, number>();
  const rotuloResponsavel = new Map<string, string>();
  const rotuloCliente = new Map<string, string>();
  const rotuloSistema = new Map<string, string>();
  const rotuloUsuario = new Map<string, string>();
  const chamados = new Map<string, TimeEntryRow>();

  for (const linha of linhas) {
    const { row, minutos, dia } = linha;

    incrementar(minutosPorDia, dia, minutos);
    incrementar(minutosPorTipo, row.type ?? '', minutos);
    incrementar(minutosPorAtendimento, row.attendanceType ?? '', minutos);
    // Agrupa por id (mesma armadilha 4.3 do how-to: dois "João" seriam contados
    // juntos se agrupássemos pelo nome). Chamado sem responsável cadastrado cai
    // no mesmo balde "Sem responsável" em vez de sumir da conta.
    const responsavelChave = row.responsibleId ?? row.responsibleName ?? '__sem_responsavel';
    incrementar(minutosPorResponsavel, responsavelChave, minutos);
    rotuloResponsavel.set(responsavelChave, row.responsibleName ?? 'Sem responsável');

    // Agrupa por id, exibe por nome (armadilha 4.3 do how-to: homônimo).
    const clienteChave = row.customerName ?? '__sem_cliente';
    incrementar(minutosPorCliente, clienteChave, minutos);
    rotuloCliente.set(clienteChave, row.customerName ?? 'Sem cliente');

    const sistemaChave = row.systemId ?? row.systemName ?? '__sem_sistema';
    incrementar(minutosPorSistema, sistemaChave, minutos);
    rotuloSistema.set(sistemaChave, row.systemName ?? 'Sem sistema');

    const usuarioChave = row.userId ?? row.userName ?? '__sem_usuario';
    incrementar(minutosPorUsuario, usuarioChave, minutos);
    rotuloUsuario.set(usuarioChave, row.userName ?? 'Não informado');

    if (row.ticketId) {
      incrementar(minutosPorChamado, row.ticketId, minutos);
      incrementar(minutosPorDiaChamado, `${dia}|${row.ticketId}`, minutos);
      if (!chamados.has(row.ticketId)) chamados.set(row.ticketId, row);

      const doDia = chamadosPorDia.get(dia) ?? new Set<string>();
      doDia.add(row.ticketId);
      chamadosPorDia.set(dia, doDia);
    }
  }

  // --- período ------------------------------------------------------------
  const primeiroDia = ctx.periodoInicio ?? linhas[0].inicio;
  const ultimoDia = ctx.periodoFim ?? linhas[linhas.length - 1].inicio;
  const uteis = diasUteis(primeiroDia, ultimoDia);
  const diasComApontamento = minutosPorDia.size;
  const capacidadeMinutos = uteis * ctx.metaDiariaHoras * 60;

  // --- barras por dia -----------------------------------------------------
  const porDia = montarPorDia(primeiroDia, ultimoDia, minutosPorDia, chamadosPorDia, ctx);

  // --- cards --------------------------------------------------------------
  const mediaPorDiaComApontamento = diasComApontamento > 0 ? totalMinutos / diasComApontamento : 0;
  const duracaoMedia = totalMinutos / linhas.length;
  const cobertura = contarPercentual(totalMinutos, capacidadeMinutos);
  const mediaChamadosDia =
    diasComApontamento > 0
      ? [...chamadosPorDia.values()].reduce((s, set) => s + set.size, 0) / diasComApontamento
      : 0;

  const cards = [
    metricCard({
      key: 'total',
      label: 'Total de horas',
      value: formatarDuracao(totalMinutos),
      rawValue: totalMinutos,
      severity: 'info',
      icon: 'pi-clock',
    }),
    metricCard({
      key: 'chamados',
      label: 'Chamados atendidos',
      value: chamados.size.toString(),
      rawValue: chamados.size,
      severity: 'neutral',
      icon: 'pi-ticket',
    }),
    metricCard({
      key: 'clientes',
      label: 'Clientes atendidos',
      value: minutosPorCliente.size.toString(),
      rawValue: minutosPorCliente.size,
      severity: 'neutral',
      icon: 'pi-building',
    }),
    metricCard({
      key: 'apontamentos',
      label: 'Apontamentos',
      value: linhas.length.toString(),
      rawValue: linhas.length,
      severity: 'neutral',
      icon: 'pi-list',
    }),
    metricCard({
      key: 'media-dia',
      label: 'Média por dia apontado',
      value: formatarDuracao(mediaPorDiaComApontamento),
      rawValue: mediaPorDiaComApontamento,
      severity: 'neutral',
      icon: 'pi-calendar',
    }),
    metricCard({
      key: 'duracao-media',
      label: 'Duração média',
      value: formatarDuracao(duracaoMedia),
      rawValue: duracaoMedia,
      severity: 'neutral',
      icon: 'pi-stopwatch',
    }),
    metricCard({
      key: 'cobertura',
      label: `Cobertura (${uteis}d úteis)`,
      value: `${cobertura}%`,
      rawValue: cobertura,
      severity: cobertura >= 90 ? 'success' : cobertura >= 70 ? 'warning' : 'danger',
      icon: 'pi-chart-pie',
    }),
    metricCard({
      key: 'em-aberto',
      label: 'Em aberto',
      value: emAberto.toString(),
      rawValue: emAberto,
      severity: emAberto > 0 ? 'danger' : 'success',
      icon: 'pi-play-circle',
    }),
  ];

  // --- distribuições ------------------------------------------------------
  // Sem alvo de clique de propósito: o recorte por cliente na API é por ID, e a
  // linha achatada só traz o NOME (`customer_name`) — filtrar por nome erraria o
  // cliente em caso de homônimo ou grafia diferente entre cadastros. Um card
  // cujo clique leva ao recorte errado custa mais confiança do que o clique
  // economiza (how-to, armadilha 4.4).
  const porCliente = fatiasPorDuracao(
    minutosPorCliente,
    rotuloCliente,
    totalMinutos,
    () => undefined
  );
  const porSistema = fatiasPorDuracao(
    minutosPorSistema,
    rotuloSistema,
    totalMinutos,
    () => undefined
  );
  // Usuário É clicável: a chave aqui é o id de verdade (agrupado por id, exibido
  // por nome), então o filtro reproduz exatamente a fatia.
  const porUsuario = fatiasPorDuracao(minutosPorUsuario, rotuloUsuario, totalMinutos, chave =>
    chave.startsWith('__') ? undefined : { user_id: chave }
  );

  // --- top chamados -------------------------------------------------------
  const topChamados: ChamadoListItem[] = [...minutosPorChamado.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_N)
    .map(([ticketId, minutos]) => {
      const row = chamados.get(ticketId);
      return {
        ticketId,
        numero: row?.ticketNumber ? `#${row.ticketNumber}` : '#—',
        resumo: resumoCurto(row?.ticketSummary),
        cliente: row?.customerName ?? 'Sem cliente',
        detalhe: formatarDuracao(minutos),
      };
    });

  return {
    isEmpty: false,
    contexto: montarContexto(linhas.length, primeiroDia, ultimoDia),
    totalDuracao: formatarDuracao(totalMinutos),
    metaDiariaHoras: ctx.metaDiariaHoras,
    cards,
    porDia,
    porTipo: fatiasFixas(minutosPorTipo, TIPOS, totalMinutos),
    porAtendimento: fatiasFixas(minutosPorAtendimento, ATENDIMENTOS, totalMinutos),
    // Sem alvo de clique: mesma justificativa de cliente — a linha achatada só
    // traz o nome do responsável, e um recorte por nome erraria em caso de
    // homônimo. `fatiasPorDuracao` (não `fatiasFixas`) porque o conjunto de
    // responsáveis não é um dicionário fixo como tipo/atendimento.
    porResponsavel: fatiasPorDuracao(
      minutosPorResponsavel,
      rotuloResponsavel,
      totalMinutos,
      () => undefined
    ),
    porCliente,
    porSistema,
    porUsuario,
    mostrarPorUsuario: minutosPorUsuario.size > 1,
    topChamados,
    estimativa: montarEstimativa(chamados, minutosPorChamado),
    qualidade: montarQualidade(linhas, mediaChamadosDia, duracaoMedia, emAberto),
    duracaoPorDiaChamado: new Map(
      [...minutosPorDiaChamado.entries()].map(([chave, minutos]) => [
        chave,
        formatarDuracao(minutos),
      ])
    ),
  };
}

function montarContexto(quantidade: number, inicio: Date, fim: Date): string {
  const plural = quantidade === 1 ? 'apontamento' : 'apontamentos';
  return `${quantidade} ${plural} · ${rotuloDia(inicio)} a ${rotuloDia(fim)}`;
}

/**
 * Uma barra por dia do período, INCLUSIVE os dias sem apontamento — é o buraco
 * que interessa ver. Um gráfico só com os dias que têm dado esconde exatamente
 * o dia esquecido.
 */
function montarPorDia(
  inicio: Date,
  fim: Date,
  minutosPorDia: Map<string, number>,
  chamadosPorDia: Map<string, Set<string>>,
  ctx: TimeEntryMetricsContext
): DayBucket[] {
  const metaMinutos = ctx.metaDiariaHoras * 60;
  const maiorDia = Math.max(...minutosPorDia.values(), metaMinutos);

  const baldes: DayBucket[] = [];
  const cursor = meiaNoite(inicio);
  const limite = meiaNoite(fim);

  // Guarda contra período absurdo vindo de filtro mal preenchido: 400 barras não
  // são um gráfico, são uma parede.
  let seguranca = 0;
  while (cursor.getTime() <= limite.getTime() && seguranca < 400) {
    seguranca++;
    const chave = chaveDia(cursor);
    const minutos = minutosPorDia.get(chave) ?? 0;
    const diaSemana = cursor.getDay();
    const fimDeSemana = diaSemana === 0 || diaSemana === 6;

    baldes.push({
      key: chave,
      label: rotuloDia(cursor),
      diaSemana: rotuloDiaSemana(cursor),
      isFimDeSemana: fimDeSemana,
      minutos,
      duracao: formatarDuracao(minutos),
      chamados: chamadosPorDia.get(chave)?.size ?? 0,
      percentMeta: Math.min(100, contarPercentual(minutos, metaMinutos)),
      alturaPercent: Math.min(100, contarPercentual(minutos, maiorDia)),
      // Fim de semana sem apontamento é o esperado, não uma falha — marcá-lo de
      // vermelho treinaria o olho a ignorar o vermelho dos dias que importam.
      severity: severidadeDia(minutos, metaMinutos, fimDeSemana),
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return baldes;
}

function severidadeDia(
  minutos: number,
  metaMinutos: number,
  fimDeSemana: boolean
): 'success' | 'warning' | 'danger' {
  if (fimDeSemana && minutos === 0) return 'success';
  if (minutos >= metaMinutos * 0.75) return 'success';
  if (minutos >= metaMinutos * 0.5) return 'warning';
  return 'danger';
}

/**
 * Estimado × realizado. Devolve `null` quando NENHUM chamado do recorte tem
 * estimativa — o bloco some em vez de exibir uma coluna de zeros que afirmaria
 * "ninguém estourou".
 */
function montarEstimativa(
  chamados: Map<string, TimeEntryRow>,
  minutosPorChamado: Map<string, number>
): EstimativaBloco | null {
  const itens: EstimativaItem[] = [];

  for (const [ticketId, row] of chamados) {
    const estimado = row.estimatedEffort ?? row.commercialEstimatedEffort ?? null;
    if (!estimado || estimado <= 0) continue;

    const minutos = minutosPorChamado.get(ticketId) ?? 0;
    const percent = contarPercentual(minutos, estimado * 60);
    itens.push({
      ticketId,
      numero: row.ticketNumber ? `#${row.ticketNumber}` : '#—',
      resumo: resumoCurto(row.ticketSummary),
      cliente: row.customerName ?? 'Sem cliente',
      apontado: formatarDuracao(minutos),
      estimado: formatarDuracao(estimado * 60),
      percentConsumo: percent,
      estourou: percent > 100,
    });
  }

  if (itens.length === 0) return null;

  itens.sort((a, b) => b.percentConsumo - a.percentConsumo);

  return {
    comEstimativa: itens.length,
    totalChamados: chamados.size,
    estouros: itens.filter(item => item.estourou).length,
    itens: itens.slice(0, TOP_N),
  };
}

function montarQualidade(
  linhas: LinhaValida[],
  mediaChamadosDia: number,
  duracaoMedia: number,
  emAberto: number
): QualidadeBloco {
  let semDescricao = 0;
  let descricaoCurta = 0;
  let blocosLongos = 0;
  let blocosCurtos = 0;
  let atravessamMeiaNoite = 0;

  for (const linha of linhas) {
    const texto = (linha.row.description ?? '').trim();
    if (!texto) semDescricao++;
    else if (texto.length < DESCRICAO_CURTA_CHARS) descricaoCurta++;

    if (linha.minutos > BLOCO_LONGO_MIN) blocosLongos++;
    if (!linha.aberto && linha.minutos < BLOCO_CURTO_MIN) blocosCurtos++;
    if (!linha.aberto && meiaNoite(linha.inicio).getTime() !== meiaNoite(linha.fim).getTime()) {
      atravessamMeiaNoite++;
    }
  }

  return {
    semDescricao,
    semDescricaoPercent: contarPercentual(semDescricao, linhas.length),
    descricaoCurta,
    blocosLongos,
    blocosCurtos,
    atravessamMeiaNoite,
    sobreposicoes: contarSobreposicoes(linhas),
    lacunas: formatarDuracao(somarLacunas(linhas)),
    chamadosPorDia: mediaChamadosDia.toFixed(1).replace('.', ','),
    duracaoMediaBloco: formatarDuracao(duracaoMedia),
    emAberto,
  };
}

/**
 * Dois apontamentos do MESMO usuário cujos intervalos se cruzam. Não é um alerta
 * de produtividade: é erro de dado — a mesma hora contada duas vezes infla todos
 * os totais acima. Comparar entre usuários diferentes seria ruído (duas pessoas
 * trabalham ao mesmo tempo por definição).
 */
function contarSobreposicoes(linhas: LinhaValida[]): number {
  const porUsuario = new Map<string, LinhaValida[]>();
  for (const linha of linhas) {
    const chave = linha.row.userId ?? linha.row.userName ?? '__sem_usuario';
    const lista = porUsuario.get(chave) ?? [];
    lista.push(linha);
    porUsuario.set(chave, lista);
  }

  let total = 0;
  for (const lista of porUsuario.values()) {
    const ordenada = [...lista].sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
    for (let i = 1; i < ordenada.length; i++) {
      if (ordenada[i].inicio.getTime() < ordenada[i - 1].fim.getTime()) total++;
    }
  }
  return total;
}

/**
 * Minutos não apontados ENTRE a primeira e a última marcação do dia, por usuário. Não mede a
 * jornada inteira de propósito: o buraco antes da primeira marcação pode ser reunião ou início
 * mais tarde; só o miolo é uma lacuna que a pessoa reconhece.
 */
function somarLacunas(linhas: LinhaValida[]): number {
  const grupos = new Map<string, LinhaValida[]>();
  for (const linha of linhas) {
    const chave = `${linha.dia}|${linha.row.userId ?? linha.row.userName ?? '__sem_usuario'}`;
    const lista = grupos.get(chave) ?? [];
    lista.push(linha);
    grupos.set(chave, lista);
  }

  let lacunas = 0;
  for (const lista of grupos.values()) {
    const ordenada = [...lista].sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
    let ultimoFim = ordenada[0].fim.getTime();
    for (let i = 1; i < ordenada.length; i++) {
      const inicio = ordenada[i].inicio.getTime();
      if (inicio > ultimoFim) lacunas += (inicio - ultimoFim) / MIN_MS;
      ultimoFim = Math.max(ultimoFim, ordenada[i].fim.getTime());
    }
  }
  return lacunas;
}

function vazio(ctx: TimeEntryMetricsContext): TimeEntryMetricsModel {
  return {
    isEmpty: true,
    contexto: 'Nenhum apontamento no recorte atual',
    totalDuracao: formatarDuracao(0),
    metaDiariaHoras: ctx.metaDiariaHoras,
    cards: [],
    porDia: [],
    porTipo: [],
    porAtendimento: [],
    porResponsavel: [],
    porCliente: [],
    porSistema: [],
    porUsuario: [],
    mostrarPorUsuario: false,
    topChamados: [],
    estimativa: null,
    qualidade: {
      semDescricao: 0,
      semDescricaoPercent: 0,
      descricaoCurta: 0,
      blocosLongos: 0,
      blocosCurtos: 0,
      atravessamMeiaNoite: 0,
      sobreposicoes: 0,
      lacunas: formatarDuracao(0),
      chamadosPorDia: '0,0',
      duracaoMediaBloco: formatarDuracao(0),
      emAberto: 0,
    },
    duracaoPorDiaChamado: new Map(),
  };
}
